import * as THREE from "three";
import { LevelSim, SimEventKind, isBeam } from "@highmarch/domain";
import type { VFX } from "../../../../../art/vfx.js";

/**
 * The Arcane Spires' held beams (TOWERS_V2, docs/TOWERS_V2.md "Arcane Spire"). While a spire holds a target
 * (`tower.beamTarget`), a beam runs from its floating `crystal` to the enemy every frame and intensifies with the sim's
 * ramp (`sim.beamRamp`, 0 just locked on → 1 full): it widens, its colour runs from a cool rose-violet to a hot
 * magenta with a white core, two charged strands start to coil round it, the glows at both ends swell and sparks spit
 * off the target on each pulse. A beam that loses its target fades out in a blink where it last stood, and a new one
 * grows out of the crystal to the next target, so a retarget reads as one beam letting go and another taking hold.
 *
 * Drawn as one dynamic mesh (camera-facing strips, additive, no depth write) plus two glow sprites a beam. Reduced
 * effects keep the beam and its ramp (they are what says the spire is working) without the strands' shimmer, the
 * sparks and the glow pulses.
 */

/** How a beam looks at ramp `r` (0–1): world widths, HDR colours and strand strength. Pure (unit-tested). */
export function beamLook(r: number): { width: number; glowWidth: number; core: number; color: [number, number, number]; strands: number; endGlow: number } {
  const k = Math.max(0, Math.min(1, r));
  const lerp = (a: number, b: number) => a + (b - a) * k;
  // a thin rose-violet thread on lock-on → a hot magenta beam (#FF4FC8, HDR) with a white core at full charge
  return {
    width: lerp(0.025, 0.06),
    glowWidth: lerp(0.08, 0.2),
    core: lerp(1.0, 2.6),
    color: [lerp(0.75, 2.2), lerp(0.25, 0.42), lerp(1.35, 1.5)],
    strands: k < 0.25 ? 0 : (k - 0.25) / 0.75,
    endGlow: lerp(0.22, 0.46),
  };
}

/** Seconds a dropped beam takes to fade, and a new beam to reach its target from the crystal. */
export const BEAM_FADE = 0.14;
export const BEAM_GROW = 0.09;
/** Segments along a strip (the strands coil, so the beam is not a single quad). */
const SEGS = 18;
/** Strips per beam: the beam itself and two strands. */
const STRIPS = 3;
/** Beams drawn at once (live + fading). */
const MAX_BEAMS = 12;

interface BeamDraw {
  towerId: number; target: number;
  readonly from: THREE.Vector3; readonly to: THREE.Vector3;
  /** Smoothed ramp, how far the tip has grown (0–1), and the beam's opacity (fades on a drop). */
  ramp: number; grow: number; alpha: number;
  /** Fading out (no longer held). */
  dying: boolean;
  seed: number;
  /** Seconds to the next spark spit. */
  sparkIn: number;
  /** Both ends have been placed at least once (a beam whose target was never drawn is dropped, never drawn to 0,0,0). */
  placed: boolean;
}

const VERT = /* glsl */ `
attribute vec3 aCol;
attribute vec3 aPrm;  // v across (-1..1), alpha, u along (0..1)
varying vec3 vCol;
varying vec3 vPrm;
void main() {
  vCol = aCol; vPrm = aPrm;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
const FRAG = /* glsl */ `
uniform float uTime;
varying vec3 vCol;
varying vec3 vPrm;
void main() {
  float v = vPrm.x, a = vPrm.y, u = vPrm.z;
  // a white-hot core a third of the strip wide, in a saturated glow that falls off to nothing at the strip's edge
  float core = exp(-v * v * 30.0);
  float glow = exp(-v * v * 4.0) * (1.0 - v * v);
  // energy running from the crystal to the target
  float flow = 0.7 + 0.3 * sin(u * 46.0 - uTime * 30.0) * sin(u * 11.0 - uTime * 7.0);
  float ends = smoothstep(0.0, 0.03, u) * smoothstep(1.0, 0.97, u);
  vec3 c = vCol * glow * flow * 0.8 + mix(vCol, vec3(1.6), 0.5) * core * 1.2;
  gl_FragColor = vec4(c * a * ends, 1.0);
}`;

function radialTexture(): THREE.Texture {
  const s = 64, c = typeof document !== "undefined" ? document.createElement("canvas") : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = s;
  const g = c.getContext("2d")!;
  const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grd.addColorStop(0, "rgba(255,255,255,1)"); grd.addColorStop(0.25, "rgba(255,255,255,0.6)"); grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd; g.fillRect(0, 0, s, s);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

const _cam = new THREE.Vector3(), _dir = new THREE.Vector3(), _side = new THREE.Vector3(), _up = new THREE.Vector3(), _c = new THREE.Vector3(), _q = new THREE.Vector3();

export class ArcaneBeams {
  readonly group = new THREE.Group();
  private readonly beams: BeamDraw[] = [];
  private readonly geo = new THREE.BufferGeometry();
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly prm: Float32Array;
  private readonly mat: THREE.ShaderMaterial;
  private readonly glows: THREE.Sprite[] = [];
  private readonly glowTex = radialTexture();
  private time = 0;
  /** Set by the owner: the crystal's world position (TowerViews.nodeWorld), and an enemy's drawn body middle. */
  towerNode: ((towerId: number, part: string) => THREE.Vector3 | null) | null = null;
  enemyAt: ((enemyId: number) => THREE.Vector3 | null) | null = null;
  /** Set by the owner: a beam locks on (sound), and the loudest beam's ramp each frame (the hum), -1 when none holds. */
  onLock: ((towerId: number) => void) | null = null;

  constructor(private readonly sim: LevelSim, private readonly fx: () => VFX | null, private readonly reduced: () => boolean, private readonly camera: () => THREE.Camera | null) {
    const verts = MAX_BEAMS * STRIPS * (SEGS + 1) * 2;
    this.pos = new Float32Array(verts * 3); this.col = new Float32Array(verts * 3); this.prm = new Float32Array(verts * 3);
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("aCol", new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("aPrm", new THREE.BufferAttribute(this.prm, 3).setUsage(THREE.DynamicDrawUsage));
    const index: number[] = [];
    for (let s = 0; s < MAX_BEAMS * STRIPS; s++) {
      const b = s * (SEGS + 1) * 2;
      for (let i = 0; i < SEGS; i++) { const a = b + i * 2; index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    }
    this.geo.setIndex(index);
    this.geo.setDrawRange(0, 0);
    this.mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: { uTime: { value: 0 } }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(this.geo, this.mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 24;
    this.group.add(mesh);
  }

  /** The beams the sim holds this tick; starts, drops and the pulses' sparks. */
  onTick(): void {
    const sim = this.sim;
    for (const ev of sim.events.all()) {
      if (ev.kind === SimEventKind.BeamStarted) {
        const old = this.beams.find((b) => !b.dying && b.towerId === ev.a);
        if (old) old.dying = true;
        this.beams.push({ towerId: ev.a, target: ev.b, from: new THREE.Vector3(), to: new THREE.Vector3(), ramp: 0, grow: 0, alpha: 1, dying: false, seed: Math.random() * 10, sparkIn: 0, placed: false });
        this.onLock?.(ev.a);
        const fx = this.reduced() ? null : this.fx();
        const at = this.towerNode?.(ev.a, "crystal");
        if (fx && at) fx.flash(at, 0.45, [1, 0.45, 0.85]);
      } else if (ev.kind === SimEventKind.BeamStopped) {
        const b = this.beams.find((x) => !x.dying && x.towerId === ev.a);
        if (b) b.dying = true;
      }
    }
    // a spire that was sold, or whose beam the sim no longer holds without saying so, lets go
    for (const b of this.beams) {
      if (b.dying) continue;
      const t = sim.findTower(b.towerId);
      if (!t || t.beamTarget !== b.target) b.dying = true;
    }
    // a spire that holds a beam the view never saw start (a resumed battle): pick it up mid-ramp
    for (const t of sim.towerSlots) {
      if (!t.alive || t.beamTarget === 0 || !isBeam(sim.balance.towers[t.archetype])) continue;
      if (!this.beams.some((b) => !b.dying && b.towerId === t.id)) this.beams.push({ towerId: t.id, target: t.beamTarget, from: new THREE.Vector3(), to: new THREE.Vector3(), ramp: sim.beamRamp(t).toNumber(), grow: 1, alpha: 1, dying: false, seed: Math.random() * 10, sparkIn: 0, placed: false });
    }
  }

  /** The loudest held beam's ramp (0–1), or -1 when no spire holds one: the hum follows it. */
  holdRamp(): number {
    let r = -1;
    for (const b of this.beams) if (!b.dying) r = Math.max(r, b.ramp);
    return r;
  }

  /** The ramp of the beam a spire holds (0 when none): its `channel` clip speeds up with it. */
  rampOf(towerId: number): number { return this.beams.find((b) => !b.dying && b.towerId === towerId)?.ramp ?? 0; }

  update(dt: number): void {
    this.time += dt;
    this.mat.uniforms.uTime.value = this.time;
    const reduced = this.reduced();
    const fx = reduced ? null : this.fx();
    const cam = this.camera();
    if (cam) cam.getWorldPosition(_cam);
    let strip = 0, glow = 0;
    for (let i = this.beams.length - 1; i >= 0; i--) {
      const b = this.beams[i];
      if (b.dying && !b.placed) { this.beams.splice(i, 1); continue; }
      if (b.dying) { b.alpha -= dt / BEAM_FADE; if (b.alpha <= 0) { this.beams.splice(i, 1); continue; } }
      else {
        const t = this.sim.findTower(b.towerId);
        const want = t ? this.sim.beamRamp(t).toNumber() : 0;
        b.ramp += (want - b.ramp) * Math.min(1, dt * 10);            // eased: the sim steps it 20 times a second
        b.grow = Math.min(1, b.grow + dt / BEAM_GROW);
        const from = this.towerNode?.(b.towerId, "crystal");
        const to = this.enemyAt?.(b.target);
        if (from) b.from.copy(from);
        if (to) b.to.copy(to);
        if (!from || !to) { if (!to) b.dying = true; continue; }
        b.placed = true;
        if (fx && (b.sparkIn -= dt) <= 0) {
          // sparks spit off the target, faster and hotter as the ramp climbs
          b.sparkIn = 0.16 - 0.1 * b.ramp;
          fx.sparks(b.to, 1 + Math.round(b.ramp * 3), { color: [1, 0.45 + 0.3 * (1 - b.ramp), 0.9], speed: 1.2 + 1.6 * b.ramp, up: 1.2, gravity: 5, life: 0.28, size: 0.06 + 0.04 * b.ramp });
          if (b.ramp > 0.85 && Math.random() < 0.25) fx.glowPulse(b.to, { size: 0.5, life: 0.18, color: [1, 0.4, 0.85] });
        }
      }
      if (!b.placed || strip + STRIPS > MAX_BEAMS * STRIPS || b.from.distanceToSquared(b.to) < 1e-4) continue;
      const look = beamLook(b.ramp);
      const tip = _c.lerpVectors(b.from, b.to, b.grow);
      strip = this.strip(strip, b.from, tip, look.glowWidth, look.color, b.alpha, 0, 0, b.seed, reduced);
      // the strands coil round the beam once it has some charge (still, under reduced effects)
      const sa = look.strands * b.alpha * 0.7;
      for (let k = 0; k < 2; k++) strip = this.strip(strip, b.from, tip, look.glowWidth * 0.35, [look.color[0] * 0.8, look.color[1] * 0.8, look.color[2] * 1.1], sa, 0.05 + 0.05 * b.ramp, k * Math.PI, b.seed, reduced);
      glow = this.glow(glow, b.from, look.endGlow * 0.8, look.color, b.alpha);
      if (b.grow >= 1) glow = this.glow(glow, b.to, look.endGlow * (1 + 0.15 * Math.sin(this.time * 31 + b.seed)), look.color, b.alpha);
    }
    this.geo.setDrawRange(0, strip * SEGS * 6);
    if (strip > 0) for (const a of ["position", "aCol", "aPrm"]) (this.geo.getAttribute(a) as THREE.BufferAttribute).needsUpdate = true;
    for (let i = glow; i < this.glows.length; i++) this.glows[i].visible = false;
  }

  /** One camera-facing strip from `a` to `b`, `amp` metres off the axis on a helix of phase `phase`. */
  private strip(at: number, a: THREE.Vector3, b: THREE.Vector3, width: number, color: readonly number[], alpha: number, amp: number, phase: number, seed: number, still: boolean): number {
    if (alpha <= 0.002) return at;
    const base = at * (SEGS + 1) * 2;
    _dir.subVectors(b, a);
    const len = _dir.length();
    _dir.multiplyScalar(1 / Math.max(1e-5, len));
    _side.subVectors(_cam, a).cross(_dir).normalize();
    _up.crossVectors(_dir, _side).normalize();
    const turns = Math.max(1, len * 1.6), spin = still ? 0 : this.time * 7;
    for (let i = 0; i <= SEGS; i++) {
      const u = i / SEGS;
      const w = Math.sin(Math.PI * u);                                  // the helix closes at both ends
      const ang = u * turns * Math.PI * 2 + phase + spin + seed;
      _q.copy(a).addScaledVector(_dir, len * u);
      if (amp > 0) _q.addScaledVector(_side, Math.cos(ang) * amp * w).addScaledVector(_up, Math.sin(ang) * amp * w);
      for (let s = 0; s < 2; s++) {
        const v = base + i * 2 + s, sign = s === 0 ? -1 : 1;
        this.pos[v * 3] = _q.x + _side.x * sign * width; this.pos[v * 3 + 1] = _q.y + _side.y * sign * width; this.pos[v * 3 + 2] = _q.z + _side.z * sign * width;
        this.col[v * 3] = color[0]; this.col[v * 3 + 1] = color[1]; this.col[v * 3 + 2] = color[2];
        this.prm[v * 3] = sign; this.prm[v * 3 + 1] = alpha; this.prm[v * 3 + 2] = u;
      }
    }
    return at + 1;
  }

  private glow(i: number, at: THREE.Vector3, size: number, color: readonly number[], alpha: number): number {
    let s = this.glows[i];
    if (!s) {
      s = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }));
      s.renderOrder = 25;
      this.glows.push(s); this.group.add(s);
    }
    s.visible = true;
    s.position.copy(at);
    s.scale.setScalar(size);
    (s.material as THREE.SpriteMaterial).color.setRGB(color[0] * 0.45, color[1] * 0.45, color[2] * 0.45);
    (s.material as THREE.SpriteMaterial).opacity = alpha;
    return i + 1;
  }

  dispose(): void {
    this.geo.dispose(); this.mat.dispose(); this.glowTex.dispose();
    for (const g of this.glows) (g.material as THREE.Material).dispose();
    this.beams.length = 0; this.glows.length = 0;
    this.group.clear();
  }
}
